import React, { useEffect, useMemo, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Box, Button, TextField, InputAdornment, Typography, Avatar, Chip,
  Checkbox, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  useTheme, alpha, Collapse, Drawer, Tabs, Tab, Skeleton, LinearProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import {
  useGetStaffDashboardStatsQuery,
  useDeleteTeamMutation,
  useUpdateTeamStatusMutation,
  useGetTeamQuery,
  useGetallLocationQuery,
  useGetallGradeQuery,
  useGetallSubjectQuery,
  useGetStaffTeachingProfileQuery,
  useGetStaffCalendarQuery,
} from "../../../Api/attoDeskApi";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import useUser from "../../../Core/hook/useUser";
import { useNotifier } from "../../../Core/Notifier";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { useSelector } from "react-redux";
import { RootState } from "../../../Store";
import Pagination from "../../../Components/Pagination";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import ReplaceImage from "../../../Images/staff.png";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import Groups2Icon from "@mui/icons-material/Groups2";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BarChartIcon from "@mui/icons-material/BarChart";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { cap, stripStaffNameSuffix, buildStaffDisplayName } from "../../../Core/util/staffName";

// Aliased for local readability. Deliberately kept as `stripLocationSuffix`
// so nothing in this file that already spelled it that way needs to change.
const stripLocationSuffix = stripStaffNameSuffix;
const fullName = (row: any) => buildStaffDisplayName(row);

// Legacy-only DISPLAY fallback. Some historical staff rows have their
// location baked into the last-name suffix (e.g. "Cruz (Ajax)",
// "St. Martin (Ajax/Oshaw, Teacher)") with no structured location_id.
// We extract *only* recognised location tokens — role tokens like
// "Teacher"/"Therapist"/"ABA" are ignored. Never written back to the DB.
const LOCATION_TOKEN_MAP: Record<string, string> = {
  ajax: "Ajax",
  scarborough: "Scarborough",
  scarb: "Scarborough",
  "scarb.": "Scarborough",
  oshawa: "Oshawa",
  oshaw: "Oshawa",
  online: "Online",
};
const inferLocationsFromName = (row: any): string[] => {
  const parts: string[] = [];
  for (const field of ["teamFirstName", "middle_name", "teamLastName"]) {
    const raw = row?.[field];
    if (typeof raw !== "string") continue;
    const m = raw.match(/\(([^)]+)\)\s*$/);
    if (m) parts.push(m[1]);
  }
  if (parts.length === 0) return [];
  const found = new Set<string>();
  for (const chunk of parts) {
    chunk.split(/[/,&]| and /i).forEach((piece) => {
      // Strip leading qualifiers like "French-" so "French-Ajax" → "Ajax".
      const cleaned = piece.trim().replace(/^[a-z]+-/i, "").trim();
      // Look at each whitespace-separated word in the piece.
      cleaned.split(/\s+/).forEach((word) => {
        const key = word.toLowerCase().replace(/[.,]$/g, "");
        const hit = LOCATION_TOKEN_MAP[key];
        if (hit) found.add(hit);
      });
    });
  }
  return Array.from(found);
};

// teams.location_id may be an int, an int-string, or a JSON-encoded int array.
// Normalise to a plain int[] so the frontend can resolve display names.
const parseLocationIds = (raw: any): number[] => {
  if (raw == null || raw === "") return [];
  if (typeof raw === "number") return [raw];
  if (Array.isArray(raw)) return raw.map((v) => Number(v)).filter((n) => !isNaN(n));
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("[")) {
      try { const arr = JSON.parse(trimmed); return Array.isArray(arr) ? arr.map((v) => Number(v)).filter((n) => !isNaN(n)) : []; } catch { return []; }
    }
    const n = Number(trimmed);
    return isNaN(n) ? [] : [n];
  }
  return [];
};

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  available: { bg: "#ecfdf5", text: "#065f46", label: "Available" },
  in_class: { bg: "#eff6ff", text: "#1e40af", label: "In Class" },
  busy: { bg: "#fffbeb", text: "#92400e", label: "Busy" },
  overloaded: { bg: "#fef2f2", text: "#991b1b", label: "Overloaded" },
};

const chipSx = { height: 22, fontSize: 11, fontWeight: 600, "& .MuiChip-label": { px: 1 } };

// ── Staff Drawer Content ─────────────────────────────────────────────────────
// Self-contained so the drawer's data lifecycle (fetch by clicked staff id,
// discard stale responses, skeleton on switch) is owned by ONE component keyed
// on the staff id. The list page just passes the clicked staff row.
interface StaffDrawerContentProps {
  staff: any;
  tab: number;
  onTabChange: (v: number) => void;
  onClose: () => void;
  onOpenFullProfile: () => void;
  onOpenOccurrence: (occurrenceId: number | string) => void;
}

const StaffDrawerContent: React.FC<StaffDrawerContentProps> = ({
  staff,
  tab,
  onTabChange,
  onClose,
  onOpenFullProfile,
  onOpenOccurrence,
}) => {
  const theme = useTheme();
  const staffId = staff?.id;
  // Fetch fresh details keyed on the clicked staff id. `skip` while we don't
  // have one; RTK Query naturally caches per-arg so a rapid click sequence
  // resolves to the LAST clicked staff's response (older responses stay in
  // cache but their subscription is dropped when the arg changes).
  const {
    data: profileRes,
    isLoading: profileLoading,
    isFetching: profileFetching,
    isError: profileError,
    refetch: refetchProfile,
  } = useGetStaffTeachingProfileQuery(
    { staffId },
    { skip: !staffId }
  );

  const today = useMemo(() => dayjs(), []);
  const rangeStart = useMemo(() => today.startOf("day").format("YYYY-MM-DD"), [today]);
  const rangeEnd = useMemo(() => today.add(30, "day").endOf("day").format("YYYY-MM-DD"), [today]);
  const {
    data: calendarRes,
    isLoading: calLoading,
    isError: calError,
    refetch: refetchCalendar,
  } = useGetStaffCalendarQuery(
    { staffId, start: rangeStart, end: rangeEnd },
    { skip: !staffId }
  );

  const profile: any = (profileRes as any)?.data || null;
  const events: any[] = Array.isArray((calendarRes as any)?.data) ? (calendarRes as any).data : [];

  const profileStaff = profile?.staff || {};
  const displayName = profileStaff.name || fullName(staff);
  const displayEmail = profileStaff.email || staff.teamEmail;
  const displayPhone = profileStaff.phone || staff.teamMobileNum;
  const displayImage = profileStaff.image || staff.teamImg;
  const displayMode = profileStaff.mode || staff.mode;
  const locations: Array<{ id: number; name: string }> = Array.isArray(profileStaff.locations) ? profileStaff.locations : [];
  const gradeSubjects: Array<{ grade_id: number; grade: string; subjects: Array<{ id: number; name: string }> }> =
    Array.isArray(profile?.grade_subjects) ? profile.grade_subjects : [];
  const counts = profile?.counts || {};
  const weekly: Record<string, any[]> = profile?.weekly_schedule || {};
  const todaySchedule: any[] = Array.isArray(profile?.today_schedule) ? profile.today_schedule : [];

  const statusLabel = statusConfig[staff.status]?.label || staff.status || "Active";
  const statusBg = statusConfig[staff.status]?.bg || "#f3f4f6";
  const statusText = statusConfig[staff.status]?.text || "#374151";
  const anyLoading = profileLoading || profileFetching;

  // ── Group calendar events into Today / Upcoming / Recurring ──
  const grouped = useMemo(() => {
    const todayStr = today.format("YYYY-MM-DD");
    const todayList = events.filter((e) => e.date === todayStr);
    const upcoming = events.filter((e) => e.date > todayStr);
    const recurring = events.filter((e) => e.is_recurring);
    return { today: todayList, upcoming, recurring };
  }, [events, today]);

  // ── Extract distinct active students from the calendar (Overview panel) ──
  const students = useMemo(() => {
    const map = new Map<number, { id: number; name: string; grade?: string; nextClass?: string }>();
    events.forEach((e) => {
      (e.students || []).forEach((s: any) => {
        if (!s?.id) return;
        if (!map.has(s.id)) {
          map.set(s.id, {
            id: s.id,
            name: s.name,
            grade: Array.isArray(e.grades) ? e.grades[0] : undefined,
            nextClass: `${dayjs(e.date).format("ddd, MMM D")} · ${fmt12(e.start_time)}`,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [events]);

  if (profileError) {
    return (
      <Box sx={{ p: 4, textAlign: "center", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Unable to load staff details.</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" size="small" onClick={() => { refetchProfile(); refetchCalendar(); }}>Retry</Button>
          <Button variant="outlined" size="small" onClick={onClose}>Close</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* ─ Sticky Drawer Header ─ */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper", p: 2.5, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
        <Avatar src={displayImage || undefined} alt={displayName}
          sx={{ width: 56, height: 56, fontSize: 20, fontWeight: 700,
            bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
          imgProps={{ onError: (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = ReplaceImage; } }}>
          {displayName.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }} noWrap>{displayName}</Typography>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
            <Chip label={statusLabel} size="small" sx={{ ...chipSx, bgcolor: statusBg, color: statusText }} />
            {locations.length > 0 && (
              <Chip
                icon={<LocationOnIcon sx={{ fontSize: "14px !important" }} />}
                label={locations.length === 1 ? locations[0].name : `${locations.length} locations`}
                size="small"
                sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.04)" }}
              />
            )}
            {typeof counts.classes === "number" && (
              <Chip label={`${counts.classes} class${counts.classes === 1 ? "" : "es"}`}
                size="small" sx={{ ...chipSx, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* ─ Sticky Tabs ─ */}
      <Tabs value={tab} onChange={(_, v) => onTabChange(v)} sx={{
        position: "sticky", top: 96, zIndex: 2, bgcolor: "background.paper",
        px: 2, borderBottom: "1px solid rgba(0,0,0,0.08)", minHeight: 42,
        "& .MuiTab-root": { fontSize: 13, fontWeight: 600, minHeight: 42, textTransform: "none", py: 0 },
      }}>
        <Tab label="Overview" />
        <Tab label="Schedule" />
        <Tab label="Classes" />
      </Tabs>

      {/* ─ Loading strip while any fetch is active ─ */}
      {anyLoading && <LinearProgress sx={{ height: 2 }} />}

      {/* ─ Tab Content ─ */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2.5 }}>
        {/* OVERVIEW */}
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Contact */}
            <SectionCard title="Contact">
              {profileLoading ? (
                <>
                  <Skeleton height={22} width="70%" />
                  <Skeleton height={22} width="55%" />
                </>
              ) : (displayPhone || displayEmail) ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {displayPhone && (
                    <Row icon={<PhoneIcon sx={{ fontSize: 16, color: "text.disabled" }} />}
                      text={formatPhoneNumberForDisplay(displayPhone)} />
                  )}
                  {displayEmail && (
                    <Row icon={<EmailOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />}
                      text={displayEmail} />
                  )}
                </Box>
              ) : (
                <Empty text="Contact information not provided." />
              )}
            </SectionCard>

            {/* Details */}
            <SectionCard title="Details">
              {profileLoading ? (
                <>
                  <Skeleton height={22} width="60%" />
                  <Skeleton height={22} width="45%" />
                </>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {displayMode && (
                    <LabeledRow label="Mode">
                      <Chip label={displayMode} size="small" sx={{ ...chipSx,
                        bgcolor: displayMode === "Online" ? "rgba(59,130,246,0.08)" : "rgba(0,0,0,0.04)",
                        color: displayMode === "Online" ? "#2563eb" : "#6b7280" }} />
                    </LabeledRow>
                  )}
                  <LabeledRow label="Location">
                    {locations.length > 0 ? (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {locations.map((l) => (
                          <Chip key={l.id} icon={<LocationOnIcon sx={{ fontSize: "14px !important" }} />}
                            label={l.name} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.04)" }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Not set</Typography>
                    )}
                  </LabeledRow>
                </Box>
              )}
            </SectionCard>

            {/* Teaching Capabilities — Subjects by Grade */}
            <SectionCard title="Teaching Capabilities">
              {profileLoading ? (
                <>
                  <Skeleton height={24} width="40%" />
                  <Skeleton height={22} width="80%" />
                </>
              ) : gradeSubjects.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {gradeSubjects.map((g) => (
                    <Box key={g.grade_id}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", mb: 0.5 }}>
                        {g.grade}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {g.subjects.map((s) => (
                          <Chip key={s.id} label={s.name} size="small"
                            sx={{ ...chipSx, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Empty text="No grade-specific teaching subjects are assigned." />
              )}
            </SectionCard>

            {/* Activity — real counts from the profile response */}
            <SectionCard title="Activity">
              <Grid container spacing={1.5}>
                <StatBox n={counts.today ?? 0} label="Today" loading={profileLoading} />
                <StatBox n={counts.this_week ?? 0} label="This Week" loading={profileLoading} />
                <StatBox n={counts.this_month ?? 0} label="This Month" loading={profileLoading} />
                <StatBox n={counts.students ?? 0} label="Active Students" loading={profileLoading} />
                <StatBox n={counts.classes ?? 0} label="Active Classes" loading={profileLoading} />
                <StatBox n={counts.subjects ?? 0} label="Subjects" loading={profileLoading} />
              </Grid>
            </SectionCard>

            {/* Students */}
            <SectionCard title="Students">
              {calLoading ? (
                <>
                  <Skeleton height={22} width="90%" />
                  <Skeleton height={22} width="70%" />
                </>
              ) : students.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {students.map((s) => (
                    <Box key={s.id}
                      onClick={() => window.open(`/student-history/${s.id}`, "_self")}
                      sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: "8px", cursor: "pointer",
                        border: "1px solid rgba(0,0,0,0.06)", "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}>
                        {(s.name || "S").charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{s.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }} noWrap>
                          {[s.grade, s.nextClass ? `Next: ${s.nextClass}` : null].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Empty text="No active students assigned." />
              )}
            </SectionCard>

            <Button fullWidth variant="outlined" size="small" onClick={onOpenFullProfile}
              startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
              sx={{ textTransform: "none", borderRadius: "8px" }}>
              View Full Profile
            </Button>
          </Box>
        )}

        {/* SCHEDULE */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <SectionCard title="Today">
              {profileLoading ? (
                <Skeleton variant="rectangular" height={64} sx={{ borderRadius: "8px" }} />
              ) : todaySchedule.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {todaySchedule.map((o: any) => (
                    <ClassCard key={o.id}
                      title={o.subject || "Class"}
                      subtitle={[o.student, o.location].filter(Boolean).join(" · ")}
                      time={`${fmt12(o.start_time)} – ${fmt12(o.end_time)}`}
                      statusLabel={o.status || "scheduled"}
                      onClick={() => onOpenOccurrence(o.id)}
                    />
                  ))}
                </Box>
              ) : (
                <Empty text="No classes scheduled for today." />
              )}
            </SectionCard>

            <SectionCard title="Weekly Teaching Schedule">
              {profileLoading ? (
                <Skeleton variant="rectangular" height={140} sx={{ borderRadius: "8px" }} />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                    const rows = weekly[day] || [];
                    if (rows.length === 0) return null;
                    return (
                      <Box key={day}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "capitalize", mb: 0.5 }}>{day}</Typography>
                        {rows.map((r: any, i: number) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.75, borderRadius: "6px", bgcolor: "rgba(0,0,0,0.02)", mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 108 }}>
                              {fmt12(r.start_time)} – {fmt12(r.end_time)}
                            </Typography>
                            <Typography sx={{ fontSize: 12, flex: 1 }} noWrap>{r.name}</Typography>
                            {typeof r.student_count === "number" && (
                              <Chip label={`${r.student_count}`} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.06)" }} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    );
                  })}
                  {Object.values(weekly).every((r: any) => !r || r.length === 0) && (
                    <Empty text="No recurring teaching schedule configured." />
                  )}
                </Box>
              )}
            </SectionCard>
          </Box>
        )}

        {/* CLASSES */}
        {tab === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {calError ? (
              <Empty text="Unable to load class occurrences. Please retry." />
            ) : (
              <>
                <SectionCard title={`Today${grouped.today.length ? ` · ${grouped.today.length}` : ""}`}>
                  {calLoading ? (
                    <Skeleton variant="rectangular" height={64} sx={{ borderRadius: "8px" }} />
                  ) : grouped.today.length > 0 ? (
                    grouped.today.map((e: any) => (
                      <ClassCard key={e.id}
                        title={[(e.grades || []).join(", "), (e.subjects || []).join(", ")].filter(Boolean).join(" → ") || "Class"}
                        subtitle={[e.class_type, e.study_mode, e.location].filter(Boolean).join(" · ")}
                        time={`${dayjs(e.date).format("ddd, MMM D")} · ${fmt12(e.start_time)} – ${fmt12(e.end_time)}`}
                        studentsInfo={typeof e.student_count === "number" ? `${e.student_count} student${e.student_count === 1 ? "" : "s"}` : undefined}
                        statusLabel={e.status || "scheduled"}
                        onClick={() => onOpenOccurrence(e.occurrence_id || e.id)}
                      />
                    ))
                  ) : (
                    <Empty text="No classes scheduled for today." />
                  )}
                </SectionCard>

                <SectionCard title={`Upcoming${grouped.upcoming.length ? ` · ${grouped.upcoming.length}` : ""}`}>
                  {calLoading ? (
                    <Skeleton variant="rectangular" height={140} sx={{ borderRadius: "8px" }} />
                  ) : grouped.upcoming.length > 0 ? (
                    grouped.upcoming.slice(0, 12).map((e: any) => (
                      <ClassCard key={e.id}
                        title={[(e.grades || []).join(", "), (e.subjects || []).join(", ")].filter(Boolean).join(" → ") || "Class"}
                        subtitle={[e.class_type, e.study_mode, e.location].filter(Boolean).join(" · ")}
                        time={`${dayjs(e.date).format("ddd, MMM D")} · ${fmt12(e.start_time)} – ${fmt12(e.end_time)}`}
                        studentsInfo={typeof e.student_count === "number" ? `${e.student_count} student${e.student_count === 1 ? "" : "s"}` : undefined}
                        statusLabel={e.status || "scheduled"}
                        onClick={() => onOpenOccurrence(e.occurrence_id || e.id)}
                      />
                    ))
                  ) : (
                    <Empty text="No upcoming classes." />
                  )}
                </SectionCard>

                {grouped.recurring.length > 0 && (
                  <SectionCard title={`Recurring · ${grouped.recurring.length}`}>
                    {grouped.recurring.slice(0, 8).map((e: any) => (
                      <ClassCard key={`rec-${e.id}`}
                        title={[(e.grades || []).join(", "), (e.subjects || []).join(", ")].filter(Boolean).join(" → ") || "Class"}
                        subtitle={[e.class_type, e.study_mode, e.location].filter(Boolean).join(" · ")}
                        time={`${dayjs(e.date).format("ddd, MMM D")} · ${fmt12(e.start_time)} – ${fmt12(e.end_time)}`}
                        studentsInfo={typeof e.student_count === "number" ? `${e.student_count} student${e.student_count === 1 ? "" : "s"}` : undefined}
                        statusLabel={e.status || "scheduled"}
                        onClick={() => onOpenOccurrence(e.occurrence_id || e.id)}
                      />
                    ))}
                  </SectionCard>
                )}

                <Button fullWidth variant="outlined" size="small" onClick={onOpenFullProfile}
                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                  sx={{ textTransform: "none", borderRadius: "8px" }}>
                  View All Class Records
                </Button>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Small presentational helpers used by StaffDrawerContent ──────────────────
const fmt12 = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  if (Number.isNaN(h)) return String(t);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ap}`;
};

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ border: "1px solid rgba(0,0,0,0.06)", borderRadius: "12px", p: 1.75, bgcolor: "#fff" }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", mb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Row: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
    {icon}
    <Typography sx={{ fontSize: 13 }} noWrap>{text}</Typography>
  </Box>
);

const LabeledRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
    <Typography sx={{ fontSize: 12, color: "text.secondary", width: 72, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontStyle: "italic" }}>{text}</Typography>
);

const StatBox: React.FC<{ n: number; label: string; loading?: boolean }> = ({ n, label, loading }) => (
  <Grid item xs={6} sm={4}>
    <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: "8px", p: 1.25, textAlign: "center" }}>
      {loading ? (
        <Skeleton height={26} sx={{ mx: "auto", maxWidth: 40 }} />
      ) : (
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{n}</Typography>
      )}
      <Typography sx={{ fontSize: 10.5, color: "text.secondary", mt: 0.25 }}>{label}</Typography>
    </Box>
  </Grid>
);

const ClassCard: React.FC<{
  title: string;
  subtitle?: string;
  time: string;
  studentsInfo?: string;
  statusLabel: string;
  onClick?: () => void;
}> = ({ title, subtitle, time, studentsInfo, statusLabel, onClick }) => (
  <Box onClick={onClick}
    sx={{ p: 1.25, mb: 1, borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { borderColor: "rgba(0,0,0,0.14)", boxShadow: "0 2px 6px rgba(0,0,0,0.04)" } : undefined }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{time}</Typography>
      <Chip label={statusLabel} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.04)", textTransform: "capitalize" }} />
    </Box>
    <Typography sx={{ fontSize: 13.5, fontWeight: 600, mt: 0.5 }} noWrap>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }} noWrap>{subtitle}</Typography>}
    {studentsInfo && (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <GroupIcon sx={{ fontSize: 13, color: "text.disabled" }} />
        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{studentsInfo}</Typography>
      </Box>
    )}
  </Box>
);

const Index = () => {
  const navigate = useNavigate();
  const { canCreate } = useUser();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { showErrorMessage, showMessage } = useNotifier();
  const { companyLogo, logowidthPrint } = useSelector((state: RootState) => state.company);

  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filterSubject, setFilterSubject] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  // Employment status filter — All | active | inactive. Defaults to All
  // so opening the page keeps showing the same 267-row list users are used to
  // (75 inactive would otherwise vanish silently on first load).
  const [filterStatus, setFilterStatus] = useState("");
  // Employment status change dialog state.
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogStaff, setStatusDialogStaff] = useState<any>(null);
  const [statusDialogNext, setStatusDialogNext] = useState<"active" | "inactive">("inactive");
  const [updateStatus, { isLoading: updatingStatus }] = useUpdateTeamStatusMutation();
  const [filterMode, setFilterMode] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "workload">("table");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [rowMenuStaff, setRowMenuStaff] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<any>(null);
  // Side-drawer state removed — clicking a staff name/card now
  // navigates directly to the full profile at `/team-history/{id}`
  // instead of opening an in-place preview drawer.

  // Data
  const { data: dashData, isLoading, isError } = useGetStaffDashboardStatsQuery({});
  const { data: locationData } = useGetallLocationQuery({ searchText: "" });
  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });
  const { data: subjectData } = useGetallSubjectQuery({ searchText: "" });
  const [deleteStaff] = useDeleteTeamMutation();

  const parsedData = useMemo(() => (dashData?.data as any) || {}, [dashData]);
  const kpis = parsedData.kpis || { totalStaff: 0, activeToday: 0, totalClassesToday: 0, avgClassesPerStaff: 0 };
  const staffList: any[] = useMemo(() => Array.isArray(parsedData.staff) ? parsedData.staff : [], [parsedData]);

  const locationList: any[] = useMemo(() => Array.isArray(locationData?.data) ? locationData!.data : [], [locationData]);
  const gradeList: any[] = useMemo(() => Array.isArray(gradeData?.data) ? gradeData!.data : [], [gradeData]);
  const subjectList: any[] = useMemo(() => Array.isArray(subjectData?.data) ? subjectData!.data : [], [subjectData]);

  const locationMap = useMemo(() => {
    const map = new Map<number, string>();
    locationList.forEach((l: any) => map.set(l.id, l.location_name));
    return map;
  }, [locationList]);

  // Unique subjects/grades from staff data
  const uniqueSubjects = useMemo(() => {
    const map = new Map<number, string>();
    staffList.forEach(s => s.subjects?.forEach((sub: string, i: number) => { if (s.subjectIds?.[i]) map.set(s.subjectIds[i], sub); }));
    return Array.from(map.entries());
  }, [staffList]);

  const uniqueGrades = useMemo(() => {
    const map = new Map<number, string>();
    staffList.forEach(s => s.grades?.forEach((g: string, i: number) => { if (s.gradeIds?.[i]) map.set(s.gradeIds[i], g); }));
    return Array.from(map.entries());
  }, [staffList]);

  // Filtering
  const filteredStaff = useMemo(() => {
    let list = staffList;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        `${s.teamFirstName} ${s.teamLastName}`.toLowerCase().includes(q) ||
        s.teamEmail?.toLowerCase().includes(q) ||
        s.teamMobileNum?.includes(q)
      );
    }
    if (filterSubject) list = list.filter(s => s.subjectIds?.includes(Number(filterSubject)));
    if (filterGrade) list = list.filter(s => s.gradeIds?.includes(Number(filterGrade)));
    if (filterLocation) {
      const wanted = Number(filterLocation);
      list = list.filter(s => parseLocationIds(s.location_id).includes(wanted));
    }
    if (filterStatus) {
      // Employment status filter — teams.status normalised. NULL → active for display purposes.
      list = list.filter(s => (s.teamStatus || "active") === filterStatus);
    }
    if (filterMode) list = list.filter(s => s.mode === filterMode);
    return list;
  }, [staffList, searchQuery, filterSubject, filterGrade, filterLocation, filterStatus, filterMode]);

  const paginatedStaff = useMemo(
    () => filteredStaff.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredStaff, page, rowsPerPage]
  );

  const activeFilterCount = [filterSubject, filterGrade, filterLocation, filterStatus, filterMode].filter(Boolean).length;

  // Selection
  const isAllSelected = paginatedStaff.length > 0 && paginatedStaff.every(s => selectedIds.has(s.id));
  const isSomeSelected = paginatedStaff.some(s => selectedIds.has(s.id));
  const handleSelectAll = () => setSelectedIds(isAllSelected ? new Set() : new Set(paginatedStaff.map(s => s.id)));
  const handleSelectRow = (id: number) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Delete
  const handleDelete = async () => {
    if (!staffToDelete) return;
    try {
      const res = await deleteStaff(String(staffToDelete.id)).unwrap();
      if (res.status) { showMessage("Staff deleted"); setDeleteDialogOpen(false); }
      else showErrorMessage(res.message || "Failed to delete");
    } catch { showErrorMessage("Failed to delete staff"); }
  };

  const clearFilters = () => { setFilterSubject(""); setFilterGrade(""); setFilterLocation(""); setFilterStatus(""); setFilterMode(""); };

  const handleStatusConfirm = async () => {
    if (!statusDialogStaff) return;
    try {
      const res = await updateStatus({ id: statusDialogStaff.id, status: statusDialogNext }).unwrap();
      if (res.status) {
        showMessage(statusDialogNext === "active" ? "Staff reactivated." : "Staff marked inactive.");
        setStatusDialogOpen(false);
        setStatusDialogStaff(null);
      } else {
        showErrorMessage(res.message || "Failed to update status.");
      }
    } catch {
      showErrorMessage("Failed to update staff status.");
    }
  };

  // Export
  const handleExportExcel = () => {
    const exportData = filteredStaff.map((row, i) => ({
      "#": i + 1, Name: fullName(row), Phone: formatPhoneNumberForDisplay(row.teamMobileNum) || "",
      Email: row.teamEmail || "", Mode: row.mode || "", Subjects: row.subjects?.join(", ") || "",
      "Today's Classes": row.todayClassCount || 0, Students: row.activeStudentCount || 0,
      Status: statusConfig[row.status]?.label || row.status || "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff");
    XLSX.writeFile(wb, `Staff_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");
    const tableData = filteredStaff.map((row, i) => [
      i + 1, fullName(row), formatPhoneNumberForDisplay(row.teamMobileNum) || "",
      row.teamEmail || "", row.subjects?.join(", ") || "", row.todayClassCount || 0,
      statusConfig[row.status]?.label || "",
    ]);
    const headers = [["#", "Name", "Phone", "Email", "Subjects", "Classes", "Status"]];
    const gen = (logoDataUrl?: string) => {
      if (logoDataUrl) {
        try { doc.addImage(logoDataUrl, "PNG", 15, 10, logowidthPrint || 30, 10); } catch { /* skip */ }
      }
      (doc as any).autoTable({ head: headers, body: tableData, startY: logoDataUrl ? 25 : 15, theme: "grid", styles: { fontSize: 8 }, headStyles: { fillColor: [99, 102, 241] } });
      doc.save(`Staff_${dayjs().format("YYYY-MM-DD")}.pdf`);
    };
    if (companyLogo) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth || img.width;
          c.height = img.naturalHeight || img.height;
          if (c.width === 0 || c.height === 0) { gen(); return; }
          c.getContext("2d")?.drawImage(img, 0, 0);
          gen(c.toDataURL("image/png"));
        } catch { gen(); }
      };
      img.onerror = () => gen();
      img.src = companyLogo;
    } else {
      gen();
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  // KPI card config
  const kpiCards = [
    { label: "Total Staff", value: kpis.totalStaff, icon: <Groups2Icon />, color: theme.palette.primary.main },
    { label: "Active Today", value: kpis.activeToday, icon: <CheckCircleIcon />, color: "#16a34a" },
    { label: "Classes Today", value: kpis.totalClassesToday, icon: <SchoolIcon />, color: "#2563eb" },
    { label: "Avg Classes/Staff", value: kpis.avgClassesPerStaff?.toFixed(1), icon: <BarChartIcon />, color: "#7c3aed" },
  ];

  const getWorkloadColor = (count: number) => {
    if (count > 40) return "#ef4444";
    if (count >= 20) return "#f59e0b";
    return "#22c55e";
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Box><Skeleton width={200} height={32} /><Skeleton width={160} height={20} /></Box>
          <Skeleton width={200} height={36} />
        </Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map(i => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton variant="rounded" height={90} sx={{ borderRadius: "12px" }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2, borderRadius: "8px" }} />
        {[0, 1, 2, 3, 4].map(i => (
          <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1, borderRadius: "8px" }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* Section 1: Header + KPI */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: theme.palette.text.primary }}>
            Staff Management
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
            Manage your teaching team
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: "16px !important" }} />}
            onClick={handleExportExcel} sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }}>Excel</Button>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon sx={{ fontSize: "16px !important" }} />}
            onClick={handleExportPDF} sx={{ color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }}>PDF</Button>
          {canCreate("Staff") && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate("/team/create")}>
            Add Staff
          </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((kpi, i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}>
            <Box sx={{
              bgcolor: "#fff", borderRadius: "12px", p: 2, display: "flex", alignItems: "center", gap: 2,
              border: "1px solid rgba(0,0,0,0.06)", borderLeft: `4px solid ${kpi.color}`,
            }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: alpha(kpi.color, 0.1), color: kpi.color, "& .MuiSvgIcon-root": { fontSize: 24 } }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, color: "text.primary" }}>{kpi.value}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 500 }}>{kpi.label}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Section 2: Search + Filters + View Toggle */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <TextField value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
          placeholder="Search by name, email, or phone..." size="small"
          sx={{ flex: 1, minWidth: 240, "& .MuiOutlinedInput-root": { bgcolor: "rgba(0,0,0,0.02)", "& fieldset": { borderColor: "rgba(0,0,0,0.08)" } } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.35)" }} /></InputAdornment>,
            endAdornment: searchQuery ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery("")}><CloseIcon sx={{ fontSize: 16 }} /></IconButton></InputAdornment> : null,
          }}
        />
        <Button variant={showFilters ? "contained" : "outlined"} size="small"
          startIcon={<FilterListIcon sx={{ fontSize: "16px !important" }} />} onClick={() => setShowFilters(!showFilters)}
          sx={{ ...(showFilters ? {} : { color: "text.secondary", borderColor: "rgba(0,0,0,0.12)" }), position: "relative" }}>
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </Button>
        <Box sx={{ display: "flex", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", overflow: "hidden" }}>
          <IconButton size="small" onClick={() => setViewMode("table")}
            sx={{ borderRadius: 0, bgcolor: viewMode === "table" ? alpha(theme.palette.primary.main, 0.1) : "transparent",
              color: viewMode === "table" ? theme.palette.primary.main : "text.secondary", px: 1.5 }}>
            <ViewListIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton size="small" onClick={() => setViewMode("workload")}
            sx={{ borderRadius: 0, bgcolor: viewMode === "workload" ? alpha(theme.palette.primary.main, 0.1) : "transparent",
              color: viewMode === "workload" ? theme.palette.primary.main : "text.secondary", px: 1.5 }}>
            <ViewModuleIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Collapsible Filters */}
      <Collapse in={showFilters}>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, p: 2, bgcolor: "rgba(0,0,0,0.015)", borderRadius: "10px",
          border: "1px solid rgba(0,0,0,0.06)", flexWrap: "wrap", alignItems: "center" }}>
          <TextField select size="small" label="Subject" value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setPage(0); }} sx={{ minWidth: 150 }}>
            <MenuItem value="">All Subjects</MenuItem>
            {uniqueSubjects.map(([id, name]) => <MenuItem key={id} value={String(id)}>{name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Grade" value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">All Grades</MenuItem>
            {uniqueGrades.map(([id, name]) => <MenuItem key={id} value={String(id)}>{name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Location" value={filterLocation} onChange={e => { setFilterLocation(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
            <MenuItem value="">All Locations</MenuItem>
            {locationList.map((l: any) => <MenuItem key={l.id} value={String(l.id)}>{l.location_name}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField select size="small" label="Mode" value={filterMode} onChange={e => { setFilterMode(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
            <MenuItem value="">All Modes</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
            <MenuItem value="Offline">Offline</MenuItem>
          </TextField>
          {activeFilterCount > 0 && <Button size="small" onClick={clearFilters} sx={{ color: "error.main", fontSize: 12 }}>Clear all</Button>}
        </Box>
      </Collapse>

      {/* Section 3A: Table View */}
      {viewMode === "table" && (
        <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "rgba(0,0,0,0.02)" }}>
                  <TableCell padding="checkbox" sx={{ pl: 2 }}>
                    <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected && !isAllSelected}
                      onChange={handleSelectAll} sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Staff Member</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>}
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Subjects</TableCell>}
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Grades</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }} align="center">Today's Classes</TableCell>
                  {!isMobile && <TableCell sx={{ fontWeight: 600 }} align="center">Students</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 60 }} align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filteredStaff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 5 : 9} align="center" sx={{ py: 8 }}>
                      <Box sx={{ opacity: 0.4 }}>
                        <GroupIcon sx={{ fontSize: 48, mb: 1 }} />
                        <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{isError ? "Error loading staff" : "No staff found"}</Typography>
                        <Typography sx={{ fontSize: 13, mt: 0.5, color: "text.secondary" }}>
                          {searchQuery ? "Try a different search term" : activeFilterCount > 0 ? "Try adjusting your filters" : "Add your first staff member"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedStaff.map(row => {
                    const isSelected = selectedIds.has(row.id);
                    const sCfg = statusConfig[row.status] || { bg: "#f3f4f6", text: "#374151", label: row.status || "Unknown" };
                    // Employment status chip — teams.status (active|inactive|null).
                    // NULL falls back to active for display; underlying row is left as-is.
                    const empStatus: "active" | "inactive" = row.teamStatus === "inactive" ? "inactive" : "active";
                    const empCfg = empStatus === "active"
                      ? { bg: "#dcfce7", text: "#166534", label: "Active" }
                      : { bg: "#fee2e2", text: "#991b1b", label: "Inactive" };
                    return (
                      <TableRow key={row.id} hover selected={isSelected} sx={{
                        cursor: "pointer",
                        "&.Mui-selected": { bgcolor: alpha(theme.palette.primary.main, 0.04) + " !important" },
                      }}>
                        <TableCell padding="checkbox" sx={{ pl: 2 }}>
                          <Checkbox size="small" checked={isSelected} onChange={() => handleSelectRow(row.id)}
                            onClick={e => e.stopPropagation()} sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }} />
                        </TableCell>
                        <TableCell onClick={() => navigate(`/team-history/${row.id}`)}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar src={row.teamImg || undefined} alt={fullName(row)}
                              sx={{ width: 38, height: 38, fontSize: 14, fontWeight: 600,
                                bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                              imgProps={{ onError: (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = ReplaceImage; } }}>
                              {fullName(row).charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary", lineHeight: 1.3,
                                "&:hover": { color: theme.palette.primary.main } }}>{fullName(row)}</Typography>
                              {isMobile && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.1 }}>
                                {row.teamEmail || ""}
                              </Typography>}
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && (() => {
                          const ids = parseLocationIds(row.location_id);
                          const structured = ids.map((id) => locationMap.get(id)).filter(Boolean) as string[];
                          const isFallback = structured.length === 0;
                          const names = isFallback ? inferLocationsFromName(row) : structured;
                          const tip = isFallback ? "Legacy location inferred from stored staff name" : "";
                          return (
                            <TableCell>
                              {names.length === 0 ? (
                                <Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>—</Typography>
                              ) : (
                                <Tooltip title={tip} disableHoverListener={!isFallback}>
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {names.slice(0, 3).map((n, i) => (
                                      <Chip key={i} icon={<LocationOnIcon sx={{ fontSize: "13px !important" }} />}
                                        label={n} size="small"
                                        sx={{ ...chipSx,
                                          bgcolor: isFallback ? "rgba(0,0,0,0.02)" : "rgba(0,0,0,0.05)",
                                          color: "text.secondary",
                                          fontStyle: isFallback ? "italic" : "normal",
                                          border: isFallback ? "1px dashed rgba(0,0,0,0.15)" : "none" }} />
                                    ))}
                                    {names.length > 3 && (
                                      <Chip label={`+${names.length - 3}`} size="small"
                                        sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary" }} />
                                    )}
                                  </Box>
                                </Tooltip>
                              )}
                            </TableCell>
                          );
                        })()}
                        {!isMobile && (
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {(row.subjects || []).slice(0, 3).map((s: string, i: number) => (
                                <Chip key={i} label={s} size="small" sx={{ ...chipSx, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
                              ))}
                              {(row.subjects || []).length > 3 && (
                                <Chip label={`+${row.subjects.length - 3} more`} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary" }} />
                              )}
                            </Box>
                          </TableCell>
                        )}
                        {!isMobile && (
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                              {(row.grades || []).slice(0, 3).map((g: string, i: number) => (
                                <Chip key={i} label={g} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary" }} />
                              ))}
                              {(row.grades || []).length > 3 && (
                                <Chip label={`+${row.grades.length - 3} more`} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.06)", color: "text.secondary" }} />
                              )}
                            </Box>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{row.todayClassCount || 0}</Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell align="center">
                            <Typography sx={{ fontSize: 13 }}>{row.activeStudentCount || 0}</Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Tooltip title={`Click to mark ${empStatus === "active" ? "inactive" : "active"}`}>
                            <Chip
                              label={empCfg.label}
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusDialogStaff(row);
                                setStatusDialogNext(empStatus === "active" ? "inactive" : "active");
                                setStatusDialogOpen(true);
                              }}
                              sx={{ ...chipSx, bgcolor: empCfg.bg, color: empCfg.text, cursor: "pointer" }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
                            <Tooltip title="View Profile">
                              {/* Real anchor (react-router Link) so the browser's native
                                  right-click → "Open in new tab" + Ctrl/middle-click work. */}
                              <IconButton size="small" component={RouterLink} to={`/team-history/${row.id}`}
                                onClick={e => { e.stopPropagation(); }}
                                sx={{ "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main } }}>
                                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Schedule">
                              {/* Opens the SELECTED staff's profile directly on
                                  the Class Schedules tab (spec-canonical
                                  `?tab=ClassSchedules`). The previous handler
                                  navigated to /staff-dashboard, which showed the
                                  logged-in user's dashboard — wrong staff, wrong
                                  page. Uses component={RouterLink} so
                                  right-click "Open in new tab" + Ctrl/middle-
                                  click behave correctly. */}
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={`/team-history/${row.id}?tab=ClassSchedules`}
                                aria-label={`View ${[row.teamFirstName, row.teamLastName].filter(Boolean).join(" ").trim() || "staff"}'s class schedules`}
                                onClick={e => { e.stopPropagation(); }}
                                sx={{ "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.08), color: theme.palette.info.main } }}>
                                <CalendarTodayIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Staff">
                              <IconButton size="small" onClick={e => { e.stopPropagation(); navigate(`/team/edit/${row.id}`); }}
                                sx={{ "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.08), color: theme.palette.info.main } }}>
                                <EditOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Message">
                              <IconButton size="small" onClick={e => { e.stopPropagation(); navigate("/message/create"); }}
                                sx={{ "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.08), color: theme.palette.info.main } }}>
                                <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={e => { e.stopPropagation(); setRowMenuAnchor(e.currentTarget); setRowMenuStaff(row); }}
                              sx={{ "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}>
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredStaff.length > rowsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredStaff.length)} of {filteredStaff.length}
              </Typography>
              <Pagination count={filteredStaff.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Box>
          )}
        </Box>
      )}

      {/* Section 3B: Workload View */}
      {viewMode === "workload" && (
        <Grid container spacing={2}>
          {filteredStaff.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ py: 8, textAlign: "center", opacity: 0.4 }}>
                <GroupIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>No staff found</Typography>
                <Typography sx={{ fontSize: 13, mt: 0.5, color: "text.secondary" }}>
                  {searchQuery ? "Try a different search term" : "Add your first staff member"}
                </Typography>
              </Box>
            </Grid>
          ) : (
            paginatedStaff.map(row => {
              const sCfg = statusConfig[row.status] || { bg: "#f3f4f6", text: "#374151", label: row.status || "Unknown" };
              const monthly = row.monthlyClassCount || 0;
              const barColor = getWorkloadColor(monthly);
              const barPercent = Math.min((monthly / 60) * 100, 100);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={row.id}>
                  <Box onClick={() => navigate(`/team-history/${row.id}`)} sx={{
                    bgcolor: "#fff", borderRadius: "12px", p: 2, cursor: "pointer",
                    border: row.status === "overloaded" ? "2px solid #ef4444" : "1px solid rgba(0,0,0,0.08)",
                    transition: "box-shadow 0.2s", "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)" },
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                      <Avatar src={row.teamImg || undefined} alt={fullName(row)}
                        sx={{ width: 42, height: 42, fontSize: 15, fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                        imgProps={{ onError: (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = ReplaceImage; } }}>
                        {fullName(row).charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {fullName(row)}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, alignItems: "center" }}>
                          <Chip label={`${row.todayClassCount || 0} classes today`} size="small" sx={{ ...chipSx, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5 }}>
                      {(row.subjects || []).slice(0, 3).map((s: string, i: number) => (
                        <Chip key={i} label={s} size="small" sx={{ ...chipSx, bgcolor: "rgba(0,0,0,0.05)", color: "text.secondary" }} />
                      ))}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Monthly workload</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{monthly} classes</Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", width: `${barPercent}%`, bgcolor: barColor, borderRadius: 3, transition: "width 0.3s" }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Chip label={sCfg.label} size="small" sx={{ ...chipSx, bgcolor: sCfg.bg, color: sCfg.text }} />
                    </Box>
                  </Box>
                </Grid>
              );
            })
          )}
          {filteredStaff.length > rowsPerPage && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Showing {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredStaff.length)} of {filteredStaff.length}
                </Typography>
                <Pagination count={filteredStaff.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Row Menu */}
      <Menu anchorEl={rowMenuAnchor} open={Boolean(rowMenuAnchor)} onClose={() => setRowMenuAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { minWidth: 180, "& .MuiMenuItem-root": { fontSize: 13, py: 1 } } }}>
        {rowMenuStaff && ((rowMenuStaff.teamStatus === "inactive") ? (
          <MenuItem onClick={() => { setStatusDialogStaff(rowMenuStaff); setStatusDialogNext("active"); setStatusDialogOpen(true); setRowMenuAnchor(null); }}>
            <ListItemIcon><CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} /></ListItemIcon>
            <ListItemText>Set Active</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={() => { setStatusDialogStaff(rowMenuStaff); setStatusDialogNext("inactive"); setStatusDialogOpen(true); setRowMenuAnchor(null); }}>
            <ListItemIcon><CheckCircleIcon sx={{ fontSize: 18, color: "warning.main" }} /></ListItemIcon>
            <ListItemText>Set Inactive</ListItemText>
          </MenuItem>
        ))}
        <MenuItem onClick={() => { if (rowMenuStaff) { setStaffToDelete(rowMenuStaff); setDeleteDialogOpen(true); } setRowMenuAnchor(null); }}
          sx={{ color: "error.main" }}>
          <ListItemIcon><DeleteOutlineIcon sx={{ fontSize: 18, color: "error.main" }} /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Employment status change dialog — Deactivate / Reactivate.
          Copies the DeletePopup MUI dialog aesthetic; deactivation
          confirmation calls out that history is preserved. */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => (updatingStatus ? undefined : setStatusDialogOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {statusDialogNext === "inactive" ? "Deactivate Staff?" : "Reactivate Staff?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {statusDialogNext === "inactive" ? (
              <>
                <strong>&ldquo;{statusDialogStaff ? fullName(statusDialogStaff) : ""}&rdquo;</strong> will be marked
                inactive.
                <Box sx={{ mt: 1.25, fontSize: 12.5, color: "text.secondary" }}>
                  This will not delete their historical records or existing class history.
                </Box>
              </>
            ) : (
              <>
                Reactivate <strong>&ldquo;{statusDialogStaff ? fullName(statusDialogStaff) : ""}&rdquo;</strong>?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            disabled={updatingStatus}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={statusDialogNext === "inactive" ? "error" : "primary"}
            onClick={handleStatusConfirm}
            disabled={updatingStatus}
          >
            {statusDialogNext === "inactive" ? "Mark Inactive" : "Make Active"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Popup */}
      <DeletePopup
        title={`Delete ${staffToDelete ? fullName(staffToDelete) : "staff"}?`}
        content="This action cannot be undone."
        open={deleteDialogOpen}
        handleCloseDelete={() => setDeleteDialogOpen(false)}
        onConfirm={() => handleDelete()}
      />

      {/* Side drawer removed — clicks now go straight to the full
          Staff Profile page at /team-history/{id}. */}
    </Box>
  );
};

export default Index;
